import { SelectionModel } from '@angular/cdk/collections';
import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { combineLatest, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { AddressModel, CarrierModel, LoadDataFacade, SetInitialStateFacade } from '../../../../../../../common/models';
import { ConfirmationDialogAfterClosedModel } from '../../../../../../../ui-kit';
import { DockBrief } from '../../../../dock';
import { HubItemFacade } from '../../../../hub/item';
import { CompartmentRuleListItem } from '../../../models';
import {
  CompartmentRuleItem,
  CompartmentRuleItemOutput,
  OverlappingRuleErrorModel,
} from '../../../models/compartment-rule-item.model';
import { CompartmentItemFacade } from '../../compartment-item.facade';
import {
  CompartmentRuleItemCreate,
  CompartmentRuleItemDelete,
  CompartmentRuleItemGetCarriers,
  CompartmentRuleItemGetDeliveryAddresses,
  CompartmentRuleItemGetDocks,
  CompartmentRuleItemGetPickupAddresses,
  CompartmentRuleItemInitialState,
  CompartmentRuleItemLoad,
  CompartmentRuleItemsDelete,
} from './compartment-rule-item.actions';
import { CompartmentRuleItemState } from './compartment-rule-item.state';

@Injectable({ providedIn: 'root' })
export class CompartmentRuleItemFacade implements LoadDataFacade, SetInitialStateFacade {
  compartmentRuleItemData$: Observable<CompartmentRuleItem> = this.store.select(
    CompartmentRuleItemState.getCompartmentRuleItem,
  );
  ruleId$: Observable<number> = this.store.select(CompartmentRuleItemState.getCompartmentRuleItemId);
  compartmentRuleItemName$: Observable<string> = this.store.select(CompartmentRuleItemState.getCompartmentRuleItemName);
  docks$: Observable<DockBrief[]> = this.store.select(CompartmentRuleItemState.getDocks);
  carriers$: Observable<CarrierModel[]> = this.store.select(CompartmentRuleItemState.getCarriers);
  pickupAddresses$: Observable<AddressModel[]> = this.store.select(CompartmentRuleItemState.getPickupAddresses);
  canLoadMorePickupAddresses$: Observable<boolean> = this.store.select(
    CompartmentRuleItemState.isMorePickupAddressesLoadable,
  );
  deliveryAddresses$: Observable<AddressModel[]> = this.store.select(CompartmentRuleItemState.getDeliveryAddresses);
  canLoadMoreDeliveryAddresses$: Observable<boolean> = this.store.select(
    CompartmentRuleItemState.isMoreDeliveryAddressesLoadable,
  );
  loaded$: Observable<boolean> = this.store.select(CompartmentRuleItemState.getLoaded);
  overlappingRuleError$: Observable<OverlappingRuleErrorModel> = this.store.select(
    CompartmentRuleItemState.getOverlappingRuleError,
  );

  constructor(
    private store: Store,
    private hubItemFacade: HubItemFacade,
    private compartmentItemFacade: CompartmentItemFacade,
  ) {}

  loadAll(ruleId: number): void {
    combineLatest([this.hubItemFacade.hubId$, this.compartmentItemFacade.compartmentId$])
      .pipe(take(1))
      .subscribe(([hubId, compartmentId]: [number, number]) => {
        this.store.dispatch(
          new CompartmentRuleItemLoad({
            hubId,
            compartmentId,
            ruleId,
          }),
        );
      });
  }

  getDocks(filter: string): void {
    combineLatest([this.hubItemFacade.hubId$, this.compartmentItemFacade.compartmentId$])
      .pipe(take(1))
      .subscribe(([hubId, compartmentId]: [number, number]) => {
        this.store.dispatch(
          new CompartmentRuleItemGetDocks({
            hubId,
            compartmentId,
            filter,
            page: 0,
            size: 10,
          }),
        );
      });
  }

  getCarriers(filter: string, limit?: number): void {
    this.store.dispatch(
      new CompartmentRuleItemGetCarriers({
        filter,
        limit: !!limit ? limit : 10,
      }),
    );
  }

  getPickupAddresses(filter: string, limit?: number): void {
    this.store.dispatch(
      new CompartmentRuleItemGetPickupAddresses({
        filter,
        limit: !!limit ? limit : 10,
      }),
    );
  }

  getDeliveryAddresses(filter: string, limit?: number): void {
    this.store.dispatch(
      new CompartmentRuleItemGetDeliveryAddresses({
        filter,
        limit: !!limit ? limit : 10,
      }),
    );
  }

  createCompartmentRule(
    body: Partial<CompartmentRuleItemOutput>,
    dialog?: MatDialogRef<ConfirmationDialogAfterClosedModel>,
  ): void {
    combineLatest([this.hubItemFacade.hubId$, this.compartmentItemFacade.compartmentId$])
      .pipe(take(1))
      .subscribe(([hubId, compartmentId]: [number, number]) => {
        this.store.dispatch(
          new CompartmentRuleItemCreate({
            hubId,
            compartmentId,
            body,
            dialog,
          }),
        );
      });
  }

  deleteCompartmentRules(
    ruleIds: number[],
    dialog: MatDialogRef<ConfirmationDialogAfterClosedModel>,
    selection: SelectionModel<CompartmentRuleListItem>,
  ): void {
    combineLatest([this.hubItemFacade.hubId$, this.compartmentItemFacade.compartmentId$])
      .pipe(take(1))
      .subscribe(([hubId, compartmentId]: [number, number]) => {
        this.store.dispatch(
          new CompartmentRuleItemsDelete({
            hubId,
            compartmentId,
            ruleIds,
            dialog,
            selection,
          }),
        );
      });
  }

  deleteCompartmentRule(ruleId: number, dialog: MatDialogRef<ConfirmationDialogAfterClosedModel>): void {
    combineLatest([this.hubItemFacade.hubId$, this.compartmentItemFacade.compartmentId$])
      .pipe(take(1))
      .subscribe(([hubId, compartmentId]: [number, number]) => {
        this.store.dispatch(
          new CompartmentRuleItemDelete({
            hubId,
            compartmentId,
            ruleIds: [ruleId],
            dialog,
          }),
        );
      });
  }

  setInitialState(): void {
    this.store.dispatch(new CompartmentRuleItemInitialState());
  }
}
